import { createSlice } from '@reduxjs/toolkit';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import { createControl } from '../../../../../domain/validation/validation';
import { default as cloneDeep } from 'lodash/cloneDeep';
import { IIndexable } from '../../../../../domain/interfaces';
import { getCountriesAndStates, USA_ID } from '../../../../../domain/addresses/addresses-provider';
import { TaxIdStatusEnum } from '../../enums/tax-id-status-enum';
import {
    getTaxIdMaxLength,
    getTaxIdMinLength, isBillingStateRequired, isCountryRequired,
    isTaxIdRequired, isTaxIdSelectedStatusRequired
} from '../../domain/shopping-cart-validations';
import { getInitialCartData } from '../data-provider';
import { getMaxMindAddressData } from '../../domain/shopping-cart-address-provider';

interface IBillingAddressState extends  IIndexable{

    // this property is used together with 'useUpdateAddressFromResponse' hook
    // to update address control on page load, and after log in.
    updateCount: number,

    billToControl: IFormControl,
    addressLine1Control: IFormControl,
    addressLine2Control: IFormControl,
    countryIdControl: IFormControl,
    stateIdControl: IFormControl,
    cityControl: IFormControl,
    zipCodeControl: IFormControl,
    phoneControl: IFormControl,
    phoneCountryIdControl: IFormControl,

    // tax id: https://wavesaudio.atlassian.net/browse/DSWEB-6
    taxIdPopupShown: boolean,
    taxIdControl: IFormControl,
    taxIdSelectedStatusControl: IFormControl,
}

const initialCartData = getInitialCartData();
const billingAddress = initialCartData ? initialCartData.billingAddress : null;

// https://wavesaudio.atlassian.net/browse/DSWEB-3281
// https://dev.maxmind.com/geoip/docs/web-services/requests
// https://github.com/maxmind/GeoIP2-dotnet/tree/main
const maxMindAddress = getMaxMindAddressData(initialCartData.maxMindAddress, getCountriesAndStates());

/**
 * initial state
 */
const initialState: IBillingAddressState = {
    updateCount: 0,

    billToControl: createControl('billTo', billingAddress?.addressPersonalName || '', {
        required: true,
        maxlength: 100,
    }),
    addressLine1Control: createControl('billingAddressLine1', billingAddress?.addressLine1 || '', {
        required: true,
        maxlength: 100,
    }),
    addressLine2Control: createControl('billingAddressLine2', billingAddress?.addressLine2 || '', {
        maxlength: 100,
    }),

    countryIdControl: createControl(
        'billingCountryId',
        Number(billingAddress?.addressCountryID) || maxMindAddress?.countryId || 0,
        {
            required:  (control) => {
                return isCountryRequired(control);
            },
        }
    ),

    stateIdControl: createControl(
        'billingStateId',
        Number(billingAddress?.addressStateID) || maxMindAddress?.stateId || 0,
        {
            /**
             * the state is required when selected country has states
             * @return {boolean} - is valid
             */
            required: (control) => {
                return isBillingStateRequired(control);
            }
        }
    ),

    cityControl: createControl('billingCity', billingAddress?.addressCity || '', { //  || maxMindAddress?.cityName
        required: true,
        maxlength: 100,
    }),
    zipCodeControl: createControl('billingZipCode', billingAddress?.addressZip || '', {
        maxlength: 10,
    }),
    phoneControl: createControl('billingPhone', billingAddress?.phoneNumber || '', {
        required: true,

        phone: true,
        phoneMessage: 'Please enter a valid number, use digits only.',

        minlength: 5,
        minlengthMessage: 'Please enter at least 5 digits.',

        maxlength: 12,
    }),
    phoneCountryIdControl: createControl('billingCountryId', Number(billingAddress?.phoneCountryID) || USA_ID, {
        required: true,
        int: true
    }),

    // tax id: https://wavesaudio.atlassian.net/browse/DSWEB-6
    taxIdPopupShown: false,

    taxIdControl: createControl(
        'taxId',
        billingAddress?.taxID || '',
        {
            /**
             * the 'taxIdSelectedStatusControl' is required when selected country has property isTaxID === true
             * @return {boolean} - is valid
             */
            required: (control) => {
                return isTaxIdRequired(control);
            },

            minlength: () => {
                return getTaxIdMinLength();
            },

            maxlength: () => {
                return getTaxIdMaxLength();
            },
        }
    ),

    /**
     * Tax id is passed to the server in any case, but it's optional for all countries except a country with
     * CMS_Country.IsTaxID = 1 (like Australia, New Zealand, etc.)
     * taxIdSelectedStatusControl represents tax id radio buttons (yes / no . undefined).
     */
    taxIdSelectedStatusControl: createControl(
        'taxIdSelectedStatus',
        billingAddress?.taxIDSelectedStatus ?? TaxIdStatusEnum.NONE,
        {
            /**
             * the 'taxIdSelectedStatusControl' is required when selected country has property isTaxID === true
             * @return {boolean} - is valid
             */
            required: (control) => {
                return isTaxIdSelectedStatusRequired(control);
            }
        }
    ),
};

const billingAddressSlice = createSlice({
    name: 'billingAddress',
    initialState,
    reducers: {
        main(state, action) {
            return {
                ...state,
                ...action.payload,
            }
        },

        setControl(state, action) {

            return {
                ...state,
                [action.payload.controlName]: cloneDeep(action.payload.control),
            }
        },

        setControlsList(state, action) {

            const updated = {...state};
            const controls = action.payload.controls;

            for(let i=0; i<controls.length; i++){
                const current = controls[i];
                updated[current.controlName] = cloneDeep(current.control);
            }

            return updated;
        }
    }
});

export default billingAddressSlice;
